import { useContext } from "react";
import { z } from "zod";

import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { useParsedQuery } from "@nens/react-query-utils";

import { API_BASE_URL } from "../constants";
import { dateSchema } from "../dates";
import { LdoPaginated, paginatedSchema } from "../pagination";

const scenarioExternalProcessingSchema = z.object({
  type: z.string(),
  id: z.number(),
  created_at: dateSchema,
  updated_at: dateSchema,
  status: z.union([
    z.literal("pending"),
    z.literal("running"),
    z.literal("finished"),
    z.literal("error"),
  ]),
  scenario_id: z.number(),
  config: z
    .object({
      raster_types: z.array(z.string()),
      prijs_peil: z.string(),
      berekenings_methods: z.string(),
    })
    .nullable(),
  errors: z.array(z.string()),
  meta_data: z.record(z.string(), z.string().or(z.number()).nullable()),
  description: z.string().nullable(),
});

export type ExternalProcessing = z.infer<typeof scenarioExternalProcessingSchema>;
export const scenarioExternalProcessingsSchema = paginatedSchema(scenarioExternalProcessingSchema);
type ExternalProcessingList = LdoPaginated<ExternalProcessing>;

export function useScenarioExternalProcessings(scenarioId: number) {
  const { apiFetchFunction, tenantId } = useContext(LdoAuthContext);
  const response = useParsedQuery<ExternalProcessingList>(
    [tenantId, "ldo-api-v1", "scenarios", "externalprocessings", scenarioId],
    () => apiFetchFunction(`${API_BASE_URL}/scenarios/${scenarioId}/external-processings`),
    (json: unknown) => scenarioExternalProcessingsSchema.parse(json)
  );
  return response;
}
